import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BottomSheet } from './BottomSheet';
import { ReauthFlow } from './ReauthFlow';
import { useHaptic } from '../../hooks/useHaptic';
import { useMe } from '../../hooks/useMe';
import { useTelegram } from '../../hooks/useTelegram';
import { hasApiAuth } from '../../lib/webSession';
import { useWallet } from '../../hooks/useWallet';
import { useTransfers } from '../../hooks/useTransfers';
import { useFeeQuote } from '../../hooks/useFeeQuote';
import { usePresaleBuy } from '../../hooks/usePresale';
import { SourceSelect, buildPaySources, pickRichestPaySource } from './SourceSelect';
import { formatPrice, presaleErrorMessage, tokensFor, type PresaleRound } from '../../lib/presale';
import {
  loadPresaleLegal,
  openPresaleLegal,
  PRESALE_AGREEMENT_URL,
  PRESALE_PRIVACY_URL,
  PRESALE_RISK_URL,
  savePresaleLegal,
  type PresaleLegalAccepted,
} from '../../lib/presaleLegal';

type Props = {
  open: boolean;
  amount: number;
  round: PresaleRound;
  onClose: () => void;
  onDone: () => void;
};

const fmtWhole = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const fmtTokens = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Confirm → send USDT TRC-20 to the dedicated presale treasury → credit allocation. */
export function PresaleConfirmSheet({
  open,
  amount,
  round,
  onClose,
  onDone,
}: Props) {
  const { t } = useTranslation();
  const { impact, notify } = useHaptic();
  const { me } = useMe();
  const { initData } = useTelegram();
  const { wallet, balances } = useWallet();
  const [sourceKey, setSourceKey] = useState<string | null>(null);
  const { send } = useTransfers();
  const buy = usePresaleBuy();
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Survives re-renders so settle can be retried without a second USDT send. */
  const [paidTransferId, setPaidTransferId] = useState<string | null>(null);
  const [legal, setLegal] = useState(loadPresaleLegal);
  const submittingRef = useRef(false);
  const payIdemRef = useRef<string | null>(null);
  const paidTransferIdRef = useRef<string | null>(null);
  /** Amount the stored payment transfer was created for — must match `amount`. */
  const paidAmountRef = useRef<number | null>(null);

  const tokens = tokensFor(amount, round);
  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;
  const sources = buildPaySources(wallet, balances, t('nav.wallet')).filter(
    (s) => s.chain === 'trx' && s.token === 'USDT',
  );
  const source = sources.find((s) => s.key === sourceKey) ?? pickRichestPaySource(sources);
  const legalOk = legal.terms && legal.jurisdiction;

  const setLegalFlag = (key: keyof PresaleLegalAccepted, value: boolean) => {
    setLegal((prev) => {
      const next = { ...prev, [key]: value };
      savePresaleLegal(next);
      return next;
    });
  };

  const markPaid = (id: string, paidAmount: number) => {
    paidTransferIdRef.current = id;
    paidAmountRef.current = paidAmount;
    setPaidTransferId(id);
  };

  const clearPaid = () => {
    paidTransferIdRef.current = null;
    paidAmountRef.current = null;
    payIdemRef.current = null;
    setPaidTransferId(null);
  };

  // If the user edits the amount after a partial success, drop the stale payment
  // so we never credit a smaller on-chain send while showing a larger quote.
  useEffect(() => {
    if (paidAmountRef.current != null && paidAmountRef.current !== amount) {
      clearPaid();
    }
  }, [amount]);

  // A different wallet is a different on-chain send. Ignore the first resolved
  // source so a balance refresh doesn't wipe a key that is already in flight.
  const prevSourceKey = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const key = source?.key ?? null;
    if (prevSourceKey.current === undefined) {
      prevSourceKey.current = key;
      return;
    }
    if (prevSourceKey.current === key) return;
    prevSourceKey.current = key;
    if (paidTransferIdRef.current || submittingRef.current) return;
    payIdemRef.current = null;
  }, [source?.key]);

  const treasury = round.treasuryTrx;
  const {
    feeUsd: networkFee,
    maxFeeUsd,
    fresh: feeFresh,
  } = useFeeQuote('trx', 'USDT', treasury ?? undefined, source?.address);
  const reserveFee = maxFeeUsd ?? networkFee ?? 0;
  const totalReserved = amount + (feeFresh ? reserveFee : 0);
  const canPay =
    !!source &&
    !!treasury &&
    !!wallet?.id &&
    feeFresh &&
    (paidTransferId ? true : source.balance >= totalReserved);

  const settlePurchase = async (transferId: string) => {
    const purchase = await buy.mutateAsync({
      paymentTransferId: transferId,
      amount,
      tokens,
      price: round.price,
    });
    notify('success');
    setToast(
      t('presale.boughtToast', {
        amount: fmtTokens(purchase.tokens),
      }),
    );
    window.setTimeout(() => setToast(null), 2200);
    clearPaid();
    onDone();
  };

  /** Credit only — payment already on-chain. Never send USDT again. */
  const retrySettle = async () => {
    const transferId = paidTransferIdRef.current;
    if (!transferId || submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      impact('medium');
      await settlePurchase(transferId);
    } catch (e) {
      notify('error');
      const msg = String(e);
      if (msg.includes('reauth_required')) {
        setReauthOpen(true);
      } else {
        setError(presaleErrorMessage(msg, t, t('presale.errGeneric')));
      }
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const runBuy = async (token?: string) => {
    if (!wallet?.id || !source || !treasury) return;
    if (paidTransferIdRef.current) return retrySettle();
    if (!legalOk) return;
    if (!round.open) {
      notify('error');
      setError(t('presale.errClosed'));
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      impact('medium');
      // Re-check before broadcasting — closing the round mid-sheet must not
      // take USDT with no allocation path (retry still works if admin reopens).
      if (!round.open) throw new Error('round_closed');
      if (!payIdemRef.current) payIdemRef.current = crypto.randomUUID();
      const transfer = await send.mutateAsync({
        walletId: wallet.id,
        chain: 'trx',
        token: 'USDT',
        to: treasury,
        amount: String(amount),
        reauthToken: token,
        fromAddressId: source.addressId,
        fromIndex: source.fromIndex,
        idempotencyKey: payIdemRef.current,
        awaitSettlement: true,
      });
      markPaid(transfer.id, amount);
      await settlePurchase(transfer.id);
    } catch (e) {
      notify('error');
      const msg = String(e);
      if (msg.includes('reauth_required')) {
        setReauthOpen(true);
      } else {
        setError(presaleErrorMessage(msg, t, t('presale.errGeneric')));
        // Keep the sheet open if USDT already left — user must be able to retry settle.
      }
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const submit = () => {
    if ((!canPay && !paidTransferId) || busy) return;
    if (paidTransferId) {
      void retrySettle();
      return;
    }
    if (!legalOk) return;
    if (needsReauth) {
      setReauthOpen(true);
      return;
    }
    void runBuy();
  };

  return (
    <>
      <BottomSheet open={open} onClose={busy || paidTransferId ? () => {} : onClose}>
        <div className="pb-2">
          <div className="mb-3.5 text-[20px] font-bold tracking-[-0.5px] text-ink">
            {t('presale.confirmBuy')}
          </div>

          <Row label={t('presale.sheetAmount')} value={fmtWhole(amount)} />
          <Row
            label={t('presale.sheetPrice')}
            value={t('presale.priceValue', { price: formatPrice(round.price) })}
          />
          <Row
            label={t('presale.rowGet')}
            value={`${fmtTokens(tokens)} ${round.symbol}`}
            valueColor="#3fa8c6"
          />
          <Row
            label={t('presale.sheetNetwork')}
            value={feeFresh ? `$${reserveFee.toFixed(2)}` : '…'}
            last
          />

          {!paidTransferId && sources.length > 0 && (
            <div className="mt-3">
              <span className="text-[13px] text-muted">{t('cards.payFrom')}</span>
              <div className="mt-2">
                <SourceSelect
                  sources={sources}
                  value={source?.key ?? null}
                  onChange={(key) => {
                    if (busy || paidTransferId) return;
                    setSourceKey(key);
                  }}
                  required={totalReserved}
                />
              </div>
            </div>
          )}

          {!paidTransferId && (
            <div className="mt-4 space-y-2.5">
              <LegalCheck
                checked={legal.terms}
                onChange={(v) => setLegalFlag('terms', v)}
                disabled={busy}
                i18nKey="presale.agreeTerms"
              />
              <LegalCheck
                checked={legal.jurisdiction}
                onChange={(v) => setLegalFlag('jurisdiction', v)}
                disabled={busy}
                i18nKey="presale.agreeJurisdiction"
              />
              <p className="px-0.5 text-[12px] leading-[1.5] text-muted">
                {t('presale.noEquityNote')}
              </p>
            </div>
          )}

          {!treasury && (
            <p className="mt-2 text-[12px] font-semibold text-negative">{t('presale.errTreasury')}</p>
          )}
          {treasury && !paidTransferId && source && source.balance < totalReserved && (
            <p className="mt-2 text-[12px] font-semibold text-negative">{t('presale.errBalance')}</p>
          )}
          {error && (
            <p className="mt-2 text-[12px] font-semibold text-negative">{error}</p>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={(!canPay && !paidTransferId) || busy || (!paidTransferId && !legalOk)}
            className="mt-4 h-12 w-full rounded-[14px] bg-ink text-[15px] font-semibold text-white active:opacity-90 disabled:opacity-50"
          >
            {busy
              ? t('common.loading')
              : paidTransferId
                ? t('common.retry')
                : t('presale.sheetConfirmBuy')}
          </button>

          <p className="mt-3.5 text-center text-[12px] leading-[1.5] text-muted">
            <DocLink url={PRESALE_AGREEMENT_URL}>{t('presale.linkTerms')}</DocLink>
            <span> · </span>
            <DocLink url={PRESALE_RISK_URL}>{t('presale.linkRisks')}</DocLink>
            <span> · </span>
            <DocLink url={PRESALE_PRIVACY_URL}>{t('presale.linkPrivacy')}</DocLink>
          </p>
        </div>
      </BottomSheet>

      {reauthOpen && hasApiAuth(initData) && (
        <div className="push-enter fixed inset-0 z-[70] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            onSuccess={(token) => {
              setReauthOpen(false);
              void runBuy(token);
            }}
            onCancel={() => setReauthOpen(false)}
          />
        </div>
      )}

      {toast && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1rem)]">
          <div className="animate-toast flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-[14px] font-medium text-white shadow-[0px_10px_30px_rgba(0,0,0,0.25)]">
            {toast}
          </div>
        </div>
      )}
    </>
  );
}

function DocLink({ url, children }: { url: string; children: ReactNode }) {
  return (
    <button
      type="button"
      className="font-medium text-[#377dff] underline"
      onClick={() => openPresaleLegal(url)}
    >
      {children}
    </button>
  );
}

function LegalCheck({
  checked,
  onChange,
  disabled,
  i18nKey,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  i18nKey: string;
}) {
  return (
    <label className="flex items-start gap-3 rounded-[14px] bg-black/[0.03] px-3 py-2.5 active:bg-black/[0.05]">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 accent-[#377dff]"
      />
      <span className="text-[13px] leading-relaxed text-muted">
        <Trans
          i18nKey={i18nKey}
          components={{
            terms: (
              <button
                type="button"
                className="font-semibold text-ink underline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openPresaleLegal(PRESALE_AGREEMENT_URL);
                }}
              />
            ),
            risk: (
              <button
                type="button"
                className="font-semibold text-ink underline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openPresaleLegal(PRESALE_RISK_URL);
                }}
              />
            ),
          }}
        />
      </span>
    </label>
  );
}

function Row({
  label,
  value,
  valueColor,
  last,
}: {
  label: string;
  value: string;
  valueColor?: string;
  last?: boolean;
}) {
  return (
    <div
      className={`flex justify-between py-[11px] text-[14px] ${
        last ? '' : 'border-b border-black/[0.06]'
      }`}
    >
      <span className="text-muted">{label}</span>
      <span className="font-semibold" style={{ color: valueColor ?? '#121212' }}>
        {value}
      </span>
    </div>
  );
}
